import {observer} from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';

import Autocomplete from 'core/components/Autocomplete.js';

/**
 * The dropdown markup style.less expects.
 *
 * Every rule for the widget's autocomplete dropdown is scoped under
 * `.hs-field .autocomplete-results-holder` - background, border, absolute
 * positioning, max-height scroll - so the list has to sit inside that
 * element. The Knockout `<auto-complete>` component rendered exactly this
 * (see directory/widget/autocomplete.js), and nothing in the widget's
 * stylesheets styles the shared component's default `ul.search-results`.
 */
export const RESULTS_HOLDER_CLASS = 'autocomplete-results-holder';
export const RESULTS_EMPTY_CLASS = 'no-results';
export const RESULTS_LIST_CLASS = 'list-unstyled results';
export const NO_RESULTS = 'No results';

export const LOCALITY_PLACEHOLDER =
    "eg. 'Sydney', 'North Shore Private' or '2000'";

/**
 * The locality dropdown's icons.
 *
 * The Knockout widget rendered Font Awesome glyphs here - an orange map
 * marker for suburbs, a blue plus-square for hospitals - via the
 * `getIcon` param in insurer_widget.html, and style.less still carries
 * `.text-blue` and `.text-orange` for them. The shared component's default
 * inline SVGs are the main directory's styling, not the widget's, and the
 * widget already loads font-awesome.less. The trailing space is the
 * `&nbsp;` the Knockout template had after the `<i>`.
 *
 * The specialty field passes no icon renderer: its Knockout config had no
 * `getIcon`, and its results carry no `type`.
 */
/**
 * The empty row, matching the Knockout `<auto-complete>` exactly: a bare `li`
 * with plain text, inside a `ul` that also carries `no-results`. The shared
 * component's default wraps the text in a `p`, whose margins make the row 32px
 * taller (81px against the original's 49px).
 */
export function renderNoResults() {
    return <li>{NO_RESULTS}</li>;
}

export function localityIcon({type}) {
    return (
        <>
            <i
                className={
                    type === 'hospital'
                        ? 'fa fa-fw fa-plus-square text-blue'
                        : 'fa fa-fw fa-map-marker text-orange'
                }
            />
            {'\u00a0'}
        </>
    );
}
export const DEFAULT_SPECIALTY_PLACEHOLDER = "eg. 'Cardiologist'";

/**
 * Search by specialty and location, the first tab of the health fund widget.
 *
 * Replaces the `#specialty` block of insurer_widget.html. It stays a real POST
 * form to the directory index: the destination reads `specialty_id`,
 * `locality_id`, `locality_type` and `health_fund` as form fields, and the
 * fund's `target` decides whether the results replace their page or open a tab.
 *
 * Two details the fund stylesheets depend on: the submit button is *hidden*
 * until both fields are chosen rather than disabled, and a chosen field is
 * marked readonly — three skins style `.hs-field input[readonly]`.
 *
 * The specialty-search widget shares this panel and needs two things the fund
 * widget does not: it posts a special interest as `special_interest_id`
 * rather than folding it into `specialty_id`, and it offers a health fund
 * select once a specialty is chosen. Both arrive as props and are off by
 * default, so the fund widget renders exactly as before.
 */
function SpecialtySearchPanel({
    disclaimer,
    healthFundName,
    healthFundsStore,
    healthFundTooltip,
    isActive,
    localityStore,
    lockedSpecialty,
    searchActionUrl,
    skin,
    specialtyPlaceholder,
    specialtyStore,
    splitSpecialInterest = false,
    target,
}) {
    const specialtyId = lockedSpecialty
        ? lockedSpecialty.id
        : specialtyStore.selected.id;
    const localityId = localityStore.selected.id;
    const canSubmit = Boolean(specialtyId && localityId);
    // `?specialty=` pins a specialty, never an interest, exactly as the
    // Knockout entry did when it seeded the observable from the hidden input.
    const isSpecialInterest = Boolean(
        splitSpecialInterest &&
            !lockedSpecialty &&
            specialtyStore.selected.type === 'interest',
    );

    React.useEffect(() => {
        if (healthFundsStore) {
            healthFundsStore.load({
                specialInterestId: isSpecialInterest ? specialtyId : null,
                specialtyId: isSpecialInterest ? null : specialtyId,
            });
        }
    }, [healthFundsStore, isSpecialInterest, specialtyId]);

    const action = skin
        ? `${searchActionUrl}?skin=${encodeURIComponent(skin)}`
        : searchActionUrl;

    return (
        <div
            aria-labelledby="specialty-tab"
            className="hs-search"
            hidden={!isActive}
            id="specialty-panel"
            role="tabpanel"
        >
            <form
                action={action}
                className="hs-form"
                data-dd-privacy="allow"
                id="search-form"
                method="post"
                role="form"
                target={target}
            >
                {lockedSpecialty ? (
                    // The embed pinned the widget to one specialty, so the
                    // field is not offered at all - only carried through.
                    <>
                        <input
                            name="specialty_id"
                            type="hidden"
                            value={lockedSpecialty.id}
                        />
                        <input
                            name="specialty"
                            type="hidden"
                            value={lockedSpecialty.name}
                        />
                    </>
                ) : (
                    <div className="hs-field">
                        <Autocomplete
                            labelClassName="hs-label"
                            labelText={
                                <>
                                    {'Enter a specialty or special interest'}
                                    <span
                                        className={
                                            specialtyId
                                                ? 'required-field hide'
                                                : 'required-field'
                                        }
                                    >
                                        {'*'}
                                    </span>
                                </>
                            }
                            emptyListClassName={RESULTS_EMPTY_CLASS}
                            highlightResults={true}
                            listClassName={RESULTS_LIST_CLASS}
                            lockOnSelect={true}
                            // What the destination actually reads. The
                            // Knockout autocomplete named this input too
                            // (`params="name: 'specialty'"`); the id fields
                            // beside it are posted but ignored.
                            name="specialty"
                            placeholder={
                                specialtyPlaceholder ||
                                DEFAULT_SPECIALTY_PLACEHOLDER
                            }
                            renderEmpty={renderNoResults}
                            store={specialtyStore}
                            showSelectionNotice={false}
                            uniqueId="specialty"
                            wrapperClassName={RESULTS_HOLDER_CLASS}
                        />
                    </div>
                )}

                <div className="hs-field">
                    <Autocomplete
                        labelClassName="hs-label"
                        labelText={
                            <>
                                {'Enter suburb, hospital or post code'}
                                <span
                                    className={
                                        localityId
                                            ? 'required-field hide'
                                            : 'required-field'
                                    }
                                >
                                    {'*'}
                                </span>
                            </>
                        }
                        emptyListClassName={RESULTS_EMPTY_CLASS}
                        highlightResults={true}
                        listClassName={RESULTS_LIST_CLASS}
                        lockOnSelect={true}
                        // Ignored by DirectorySearchForm, which works off
                        // locality_id - but the Knockout input carried it.
                        name="search-locality"
                        placeholder={LOCALITY_PLACEHOLDER}
                        renderEmpty={renderNoResults}
                        renderIcon={localityIcon}
                        store={localityStore}
                        showSelectionNotice={false}
                        uniqueId="search-locality"
                        wrapperClassName={RESULTS_HOLDER_CLASS}
                    />
                    {!lockedSpecialty && (
                        <input
                            id="specialty-id"
                            name="specialty_id"
                            type="hidden"
                            value={(isSpecialInterest ? '' : specialtyId) || ''}
                        />
                    )}
                    {splitSpecialInterest && !lockedSpecialty && (
                        <input
                            id="special-interest-id"
                            name="special_interest_id"
                            type="hidden"
                            value={(isSpecialInterest && specialtyId) || ''}
                        />
                    )}
                    <input
                        id="locality-type"
                        name="locality_type"
                        type="hidden"
                        value={localityStore.selected.type || ''}
                    />
                    <input
                        id="locality-id"
                        name="locality_id"
                        type="hidden"
                        value={localityId || ''}
                    />
                    {/*
                      * Only one field may carry this name. widget.html
                      * renders both the hidden input and the select, and gets
                      * away with it because the view never populates the
                      * hidden one and Django reads the last value - so the
                      * select wins by luck. Here the select replaces it.
                      */}
                    {!healthFundsStore && (
                        <input
                            id="health_fund"
                            name="health_fund"
                            type="hidden"
                            value={healthFundName || ''}
                        />
                    )}
                </div>

                {healthFundsStore && healthFundsStore.funds.length > 0 && (
                    <div className="hs-field margin-sm-bottom form-group-lg">
                        <label className="hs-label" htmlFor="health-fund">
                            {'Select health fund'}
                            <span className="text-muted">{' (optional)'}</span>
                            {healthFundTooltip && (
                                /*
                                 * A plain `title`, deliberately. widget.html
                                 * carries `data-bind="tooltip: true"`, but
                                 * that handler lives in
                                 * static/js/ko/bindings/tooltip.js and is not
                                 * in this widget's bundle - so it never
                                 * registers, the attribute is never removed,
                                 * and what a member actually sees today is
                                 * the browser's own tooltip.
                                 */
                                <span title={healthFundTooltip}>
                                    {' '}<i className="fa fa-info-circle" />
                                </span>
                            )}
                        </label>
                        <select
                            className={
                                healthFundsStore.selectedName
                                    ? 'hs-field__select'
                                    : 'hs-field__select hs-field__select--placeholder'
                            }
                            id="health-fund"
                            name="health_fund"
                            onChange={(event) =>
                                healthFundsStore.select(event.target.value)
                            }
                            value={healthFundsStore.selectedName}
                        >
                            <option value="">
                                {'No health fund selected'}
                            </option>
                            {healthFundsStore.funds.map((fund) => (
                                <option key={fund.id} value={fund.name}>
                                    {fund.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {disclaimer && (
                    <span className="hs-disclaimer" id="disclaimer_notification">
                        {`* ${disclaimer}`}
                    </span>
                )}

                <div className="hs-button-row">
                    <button
                        className={canSubmit ? 'hs-btn' : 'hs-btn hide'}
                        type="submit"
                    >
                        {'Search'}
                    </button>
                </div>
            </form>
        </div>
    );
}

SpecialtySearchPanel.propTypes = {
    // HealthFund.iframe_disclaimer. Absent for funds that have not set one.
    disclaimer: PropTypes.string,
    healthFundName: PropTypes.string,
    // Given only by the specialty-search widget: its select is populated from
    // the API once a specialty is chosen, rather than fixed by the embed.
    healthFundsStore: PropTypes.object,
    // Server-rendered, because the wording differs for professional users.
    healthFundTooltip: PropTypes.string,
    isActive: PropTypes.bool,
    localityStore: PropTypes.object.isRequired,
    // Set when the embed carries ?specialty=, pinning the widget to one.
    lockedSpecialty: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
    }),
    searchActionUrl: PropTypes.string.isRequired,
    skin: PropTypes.string,
    // HealthFund.specialty_placeholder.
    specialtyPlaceholder: PropTypes.string,
    specialtyStore: PropTypes.object.isRequired,
    // Post a chosen special interest as `special_interest_id`. The fund
    // widget's destination does not read it; this widget's does.
    splitSpecialInterest: PropTypes.bool,
    target: PropTypes.string,
};

export default observer(SpecialtySearchPanel);
